import React, { useEffect, useState } from "react"
import axios from "axios"
const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"

interface LinhaConformidade {
  modelo_id: string
  modelo_nome: string
  ativo_id: string
  ativo_nome: string
  placa: string | null
  data: string
  status: "conforme" | "pendente" | "sem_uso"
}

const hoje = () => new Date().toISOString().slice(0, 10)
const diasAtras = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10)

export default function ChecklistConformidade() {
  const [linhas, setLinhas] = useState<LinhaConformidade[]>([])
  const [carregando, setCarregando] = useState(true)
  const [dataInicio, setDataInicio] = useState(diasAtras(7))
  const [dataFim, setDataFim] = useState(hoje())
  const [busca, setBusca] = useState("")

  const token = () => localStorage.getItem("moobi_token")
  const headers = () => ({ Authorization: `Bearer ${token()}` })

  const carregar = async () => {
    setCarregando(true)
    try {
      const r = await axios.get(`${API}/checklists/conformidade`, {
        headers: headers(), params: { data_inicio: dataInicio, data_fim: dataFim },
      })
      setLinhas(r.data)
    } catch { setLinhas([]) }
    setCarregando(false)
  }

  useEffect(() => { carregar() }, [])

  const corFundo = "#0d1b2e", corCard = "#1e2f4a", corBorda = "#2d4166", corTexto = "#f1f5f9", corMuted = "#94a3b8", corAccent = "#0ea5e9"
  const estiloInput: React.CSSProperties = {
    padding: "9px 12px", borderRadius: 8, border: `1px solid ${corBorda}`,
    background: corCard, color: corTexto, fontSize: 13, boxSizing: "border-box",
  }

  const termo = busca.trim().toLowerCase()
  const linhasFiltradas = linhas.filter(l =>
    !termo || `${l.ativo_nome} ${l.placa || ""}`.toLowerCase().includes(termo)
  )
  const diasUnicos = Array.from(new Set(linhasFiltradas.map(l => l.data))).sort()
  const veiculosMap = new Map<string, { nome: string; placa: string | null; modelo: string; dias: Record<string, string> }>()
  linhasFiltradas.forEach(l => {
    const chave = `${l.ativo_id}__${l.modelo_id}`
    if (!veiculosMap.has(chave)) {
      veiculosMap.set(chave, { nome: l.ativo_nome, placa: l.placa, modelo: l.modelo_nome, dias: {} })
    }
    veiculosMap.get(chave)!.dias[l.data] = l.status
  })
  const veiculosLista = Array.from(veiculosMap.values())

  const pendentesCount = linhasFiltradas.filter(l => l.status === "pendente").length

  const corStatus = (s: string | undefined) => {
    if (s === "conforme") return { bg: "rgba(34,197,94,0.2)", fg: "#4ade80", label: "✓" }
    if (s === "pendente") return { bg: "rgba(239,68,68,0.25)", fg: "#f87171", label: "!" }
    if (s === "sem_uso") return { bg: "rgba(100,116,139,0.15)", fg: "#64748b", label: "—" }
    return { bg: "transparent", fg: corMuted, label: "" }
  }

  const fmtDataCurta = (d: string) => {
    const [, m, dd] = d.split("-")
    return `${dd}/${m}`
  }

  if (carregando) return <div style={{ padding: 32, color: corMuted }}>Carregando...</div>

  return (
    <div style={{ padding: 24, height: "100%", overflow: "auto", background: corFundo, color: corTexto }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Conformidade de Checklist</h2>
      <p style={{ fontSize: 12, color: corMuted, marginBottom: 20 }}>
        Cruza a regra de dias obrigatórios com o uso real do veículo — mostra quem rodou sem fazer o checklist.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={estiloInput} type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        <span style={{ color: corMuted, fontSize: 12 }}>até</span>
        <input style={estiloInput} type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        <button onClick={carregar} style={{ ...estiloInput, background: corAccent, border: "none", cursor: "pointer", fontWeight: 600 }}>
          Filtrar
        </button>
        <input style={{ ...estiloInput, minWidth: 220 }} placeholder="Buscar veículo ou placa..."
          value={busca} onChange={e => setBusca(e.target.value)} />
        {pendentesCount > 0 && (
          <span style={{ fontSize: 12, color: "#f87171", fontWeight: 600, marginLeft: "auto" }}>
            ⚠️ {pendentesCount} ocorrência(s) de veículo rodando sem checklist
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 11, color: corMuted }}>
        <span><span style={{ color: "#4ade80" }}>✓</span> Conforme</span>
        <span><span style={{ color: "#f87171" }}>!</span> Rodou sem checklist</span>
        <span><span style={{ color: "#64748b" }}>—</span> Sem uso (não precisava)</span>
      </div>

      {veiculosLista.length === 0 ? (
        <div style={{ color: corMuted, fontSize: 13 }}>
          Nenhum modelo de checklist tem dias da semana configurados ainda, ou nenhum veículo coberto por essas regras.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ position: "sticky", left: 0, background: corFundo, textAlign: "left", padding: "8px 12px", color: corMuted, fontWeight: 400, minWidth: 180 }}>Veículo</th>
                {diasUnicos.map(d => (
                  <th key={d} style={{ padding: "8px 6px", color: corMuted, fontWeight: 400, minWidth: 36, textAlign: "center" }}>{fmtDataCurta(d)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {veiculosLista.map((v, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${corCard}` }}>
                  <td style={{ position: "sticky", left: 0, background: corFundo, padding: "8px 12px" }}>
                    <div style={{ fontWeight: 600 }}>{v.nome}</div>
                    <div style={{ fontSize: 10, color: corMuted }}>{v.placa} · {v.modelo}</div>
                  </td>
                  {diasUnicos.map(d => {
                    const s = corStatus(v.dias[d])
                    return (
                      <td key={d} style={{ padding: 4, textAlign: "center" }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: s.bg, color: s.fg,
                          display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, margin: "0 auto" }}>
                          {s.label}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
